(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ml-aop-root]';
  const STORAGE_PREFIX = 'ml_aop_';
  const AUDIENCE_KEY = `${STORAGE_PREFIX}audience_v1`;
  const SESSION_START_KEY = `${STORAGE_PREFIX}session_started_at_v1`;
  const PENDING_KEY = `${STORAGE_PREFIX}pending_submit_v1`;
  const PENDING_TTL_MS = 15 * 60 * 1000;
  const DAY_MS = 24 * 60 * 60 * 1000;

  const safeStorage = (type) => {
    try {
      const storage = window[type];
      const testKey = `${STORAGE_PREFIX}storage_test`;
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return null;
    }
  };

  const localStore = safeStorage('localStorage');
  const sessionStore = safeStorage('sessionStorage');

  const readJSON = (storage, key) => {
    if (!storage) return null;
    try {
      const raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  const writeJSON = (storage, key, value) => {
    if (!storage) return false;
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeItem = (storage, key) => {
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch (error) {
      // Storage can be unavailable in restricted browser contexts.
    }
  };

  const readString = (storage, key) => {
    if (!storage) return null;
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeString = (storage, key, value) => {
    if (!storage) return false;
    try {
      storage.setItem(key, String(value));
      return true;
    } catch (error) {
      return false;
    }
  };

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const bool = (value) => String(value) === 'true';

  const parseConfig = (root) => {
    const node = root.querySelector('[data-ml-aop-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const normalizePath = (input, rootUrl = '/') => {
    let url;

    try {
      url = new URL(input || '/', window.location.origin);
    } catch (error) {
      return '/';
    }

    if (url.origin !== window.location.origin) {
      return null;
    }

    let path = url.pathname || '/';
    path = path.replace(/\/{2,}/g, '/');

    let localeRoot = '/';
    try {
      localeRoot = new URL(rootUrl || '/', window.location.origin).pathname || '/';
    } catch (error) {
      localeRoot = '/';
    }

    localeRoot = localeRoot.replace(/\/+$/, '') || '/';

    if (
      localeRoot !== '/' &&
      (path === localeRoot || path.startsWith(`${localeRoot}/`))
    ) {
      path = path.slice(localeRoot.length) || '/';
    }

    if (!path.startsWith('/')) {
      path = `/${path}`;
    }

    if (path.length > 1) {
      path = path.replace(/\/+$/, '');
    }

    return path || '/';
  };

  const parsePathList = (raw, rootUrl) => {
    return String(raw || '')
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => normalizePath(item, rootUrl))
      .filter(Boolean);
  };

  const getAudienceFromEntry = (config) => {
    const path = normalizePath(window.location.href, config.rootUrl);
    if (!path) return 'c';

    const exactPaths = parsePathList(config.businessExactPaths, config.rootUrl);
    const prefixes = parsePathList(config.businessPrefixes, config.rootUrl);

    if (exactPaths.includes(path)) {
      return 'b';
    }

    if (prefixes.some((prefix) => path.startsWith(prefix))) {
      return 'b';
    }

    if (path === '/blogs' || path.startsWith('/blogs/')) {
      return 'blog';
    }

    return 'c';
  };

  const getLockedAudience = (config) => {
    if (config.designMode) {
      return getAudienceFromEntry(config);
    }

    const stored = readString(localStore, AUDIENCE_KEY);
    if (['c', 'b', 'blog'].includes(stored)) {
      return stored;
    }

    const detected = getAudienceFromEntry(config);
    writeString(localStore, AUDIENCE_KEY, detected);
    return detected;
  };

  const doneKey = (audience, version) =>
    `${STORAGE_PREFIX}done_${audience}_v${version}`;

  const closedKey = (audience, version) =>
    `${STORAGE_PREFIX}closed_${audience}_v${version}`;

  const shownKey = (audience, version) =>
    `${STORAGE_PREFIX}shown_${audience}_v${version}`;

  const getPending = () => {
    const pending = readJSON(sessionStore, PENDING_KEY);
    if (!pending) return null;

    const submittedAt = Number(pending.submittedAt || 0);
    if (!submittedAt || Date.now() - submittedAt > PENDING_TTL_MS) {
      removeItem(sessionStore, PENDING_KEY);
      return null;
    }

    return pending;
  };

  const cleanSuccessParameters = () => {
    const url = new URL(window.location.href);
    let changed = false;

    ['ml_popup_success', 'ml_popup_version'].forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, document.title, next);
    }
  };

  const processOfferSuccessHandshake = (config) => {
    const url = new URL(window.location.href);
    const audience = url.searchParams.get('ml_popup_success');
    const version = url.searchParams.get('ml_popup_version');

    if (!audience || !version) {
      return false;
    }

    const pending = getPending();
    const currentPath = normalizePath(window.location.href, config.rootUrl);
    const pendingPath = pending
      ? normalizePath(pending.offerUrl, config.rootUrl)
      : null;

    const valid =
      pending &&
      pending.audience === audience &&
      String(pending.version) === String(version) &&
      currentPath &&
      pendingPath &&
      currentPath === pendingPath;

    if (valid && !config.designMode) {
      writeString(localStore, doneKey(audience, version), '1');
      removeItem(sessionStore, PENDING_KEY);
    }

    cleanSuccessParameters();
    return valid;
  };

  const addSuccessParameters = (offerUrl, audience, version) => {
    const url = new URL(offerUrl, window.location.origin);
    url.searchParams.set('ml_popup_success', audience);
    url.searchParams.set('ml_popup_version', String(version));

    return `${url.pathname}${url.search}${url.hash}`;
  };

  const redirectSuccessfulSubmission = (pending) => {
    if (!pending || !pending.offerUrl) return;
    const destination = addSuccessParameters(
      pending.offerUrl,
      pending.audience,
      pending.version
    );
    window.location.replace(destination);
  };

  const isStronglyExcludedPath = (path) => {
    if (!path) return true;

    const exact = new Set([
      '/cart',
      '/checkout',
      '/account',
      '/challenge',
      '/password',
      '/thank_you'
    ]);

    if (exact.has(path)) {
      return true;
    }

    const prefixes = [
      '/cart/',
      '/checkout/',
      '/checkouts/',
      '/account/',
      '/challenge/',
      '/orders/',
      '/thank_you/'
    ];

    return prefixes.some((prefix) => path.startsWith(prefix));
  };

  const getOfferPaths = (root, config) => {
    return Array.from(root.querySelectorAll('[data-ml-aop-panel]'))
      .map((panel) => panel.dataset.offerUrl)
      .filter(Boolean)
      .map((url) => normalizePath(url, config.rootUrl))
      .filter(Boolean);
  };

  const isOfferTargetPage = (root, config, currentPath) => {
    const targets = getOfferPaths(root, config);
    return targets.includes(currentPath);
  };

  const isPageAllowed = (panel, pageType) => {
    const map = {
      index: 'showIndex',
      product: 'showProduct',
      collection: 'showCollection',
      page: 'showPage',
      blog: 'showBlog',
      article: 'showArticle',
      search: 'showSearch'
    };

    const dataKey = map[pageType];
    if (!dataKey) return false;
    return bool(panel.dataset[dataKey]);
  };

  const isPanelEligible = (root, panel, config) => {
    if (!panel || !bool(panel.dataset.enabled) || !bool(panel.dataset.ready)) {
      return false;
    }

    const currentPath = normalizePath(window.location.href, config.rootUrl);

    if (isStronglyExcludedPath(currentPath)) {
      return false;
    }

    if (isOfferTargetPage(root, config, currentPath)) {
      return false;
    }

    return isPageAllowed(panel, config.pageType);
  };

  const isComplete = (panel, config) => {
    if (config.designMode) return false;
    const audience = panel.dataset.audience;
    const version = panel.dataset.version;
    return readString(localStore, doneKey(audience, version)) === '1';
  };

  const isCoolingDown = (panel, config) => {
    if (config.designMode) return false;

    const audience = panel.dataset.audience;
    const version = panel.dataset.version;
    const cooldownDays = Number(panel.dataset.cooldownDays || 0);

    if (cooldownDays <= 0) {
      return false;
    }

    const closedAt = Number(
      readString(localStore, closedKey(audience, version)) || 0
    );

    return closedAt > 0 && Date.now() - closedAt < cooldownDays * DAY_MS;
  };

  const wasShownThisSession = (panel, config) => {
    if (config.designMode) return false;

    return (
      readString(
        sessionStore,
        shownKey(panel.dataset.audience, panel.dataset.version)
      ) === '1'
    );
  };

  const getSessionStartedAt = (config) => {
    if (config.designMode) {
      return Date.now();
    }

    const existing = Number(readString(sessionStore, SESSION_START_KEY) || 0);
    if (existing > 0) {
      return existing;
    }

    const now = Date.now();
    writeString(sessionStore, SESSION_START_KEY, now);
    return now;
  };

  const markShown = (panel, config) => {
    if (config.designMode) return;
    writeString(
      sessionStore,
      shownKey(panel.dataset.audience, panel.dataset.version),
      '1'
    );
  };

  const markClosed = (panel, config) => {
    if (config.designMode) return;
    writeString(
      localStore,
      closedKey(panel.dataset.audience, panel.dataset.version),
      Date.now()
    );
  };

  const setClientError = (panel, message) => {
    const error = panel.querySelector('[data-ml-aop-client-error]');
    if (!error) return;

    if (!message) {
      error.hidden = true;
      error.textContent = '';
      return;
    }

    error.textContent = message;
    error.hidden = false;
  };

  const setSubmitting = (form, submitting) => {
    const button = form.querySelector('[data-ml-aop-submit]');
    if (!button) return;

    button.disabled = submitting;
    button.setAttribute('aria-busy', submitting ? 'true' : 'false');

    const label = button.querySelector('[data-ml-aop-submit-label]');
    if (label) {
      if (!button.dataset.mlAopOriginalLabel) {
        button.dataset.mlAopOriginalLabel = label.textContent;
      }
      label.textContent = submitting ? 'Submitting...' : button.dataset.mlAopOriginalLabel;
    }
  };

  const createController = (root, config) => {
    let activePanel = null;
    let previousFocus = null;
    let timerId = null;

    const panels = Array.from(root.querySelectorAll('[data-ml-aop-panel]'));

    const findPanel = (audience) =>
      root.querySelector(
        `[data-ml-aop-panel][data-audience="${escapeSelector(audience)}"]`
      );

    const hidePanel = (options = {}) => {
      if (!activePanel) return;

      const panelToClose = activePanel;

      if (options.userInitiated) {
        markClosed(panelToClose, config);
      }

      panelToClose.hidden = true;
      root.hidden = true;
      document.body.classList.remove('ml-aop-is-open');
      activePanel = null;

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }

      previousFocus = null;
    };

    const showPanel = (panel, options = {}) => {
      if (!panel) return false;

      if (!options.force && !isPanelEligible(root, panel, config)) {
        return false;
      }

      if (!options.force) {
        if (isComplete(panel, config) || isCoolingDown(panel, config)) {
          return false;
        }

        if (wasShownThisSession(panel, config)) {
          return false;
        }
      }

      panels.forEach((item) => {
        item.hidden = item !== panel;
      });

      previousFocus = document.activeElement;
      activePanel = panel;
      root.hidden = false;
      document.body.classList.add('ml-aop-is-open');

      if (!options.skipMarkShown) {
        markShown(panel, config);
      }

      window.setTimeout(() => {
        const email = panel.querySelector('[data-ml-aop-email]');
        if (email && !config.designMode) {
          email.focus({ preventScroll: true });
        }
      }, 30);

      return true;
    };

    const schedulePanel = (panel) => {
      if (!panel || !isPanelEligible(root, panel, config)) return;
      if (isComplete(panel, config) || isCoolingDown(panel, config)) return;
      if (wasShownThisSession(panel, config)) return;

      const delayMs = Math.max(0, Number(panel.dataset.delaySeconds || 0) * 1000);
      const sessionStartedAt = getSessionStartedAt(config);
      const elapsed = Math.max(0, Date.now() - sessionStartedAt);
      const remaining = Math.max(0, delayMs - elapsed);

      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        if (isPanelEligible(root, panel, config)) {
          showPanel(panel);
        }
      }, remaining);
    };

    const handleOverlayClick = (event) => {
      if (!activePanel) return;
      if (event.target === event.currentTarget) {
        hidePanel({ userInitiated: true });
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape' && activePanel) {
        event.preventDefault();
        hidePanel({ userInitiated: true });
      }
    };

    const handleCloseClick = (event) => {
      const button = event.target.closest('[data-ml-aop-close]');
      if (!button) return;

      const panel = button.closest('[data-ml-aop-panel]');
      if (panel && panel === activePanel) {
        hidePanel({ userInitiated: true });
      }
    };

    const handleSubmit = (event) => {
      const form = event.target.closest('.ml-aop__form');
      if (!form || !root.contains(form)) return;

      const panel = form.closest('[data-ml-aop-panel]');
      if (!panel) return;

      setClientError(panel, '');

      if (config.designMode) {
        event.preventDefault();
        setClientError(
          panel,
          'Preview mode: form submission is disabled. Test the live theme preview to submit an email.'
        );
        return;
      }

      const offerUrl = panel.dataset.offerUrl;
      const normalizedOfferPath = normalizePath(offerUrl, config.rootUrl);

      if (!offerUrl || !normalizedOfferPath) {
        event.preventDefault();
        setClientError(
          panel,
          'This offer is not available yet. Please try again later.'
        );
        return;
      }

      const email = form.querySelector('[data-ml-aop-email]');
      if (!email || !email.value.trim()) {
        event.preventDefault();
        setClientError(panel, 'Please enter your email address.');
        return;
      }

      const pending = {
        audience: panel.dataset.audience,
        version: panel.dataset.version,
        offerUrl,
        submittedAt: Date.now(),
        email: email.value.trim()
      };

      if (!writeJSON(sessionStore, PENDING_KEY, pending)) {
        event.preventDefault();
        setClientError(
          panel,
          'Your browser blocked temporary storage needed to continue. Please enable site storage and try again.'
        );
        return;
      }

      setSubmitting(form, true);
    };

    const getPanelFormState = (panel) => {
      const state = panel
        ? panel.querySelector('[data-ml-aop-form-state]')
        : null;

      return {
        postedSuccessfully: state
          ? bool(state.dataset.postedSuccessfully)
          : false,
        hasErrors: state ? bool(state.dataset.hasErrors) : false
      };
    };

    const handlePendingSubmissionReturn = () => {
      const pending = getPending();
      if (!pending) return false;

      const params = new URLSearchParams(window.location.search);
      const panel = findPanel(pending.audience);
      const formState = getPanelFormState(panel);

      if (
        params.get('customer_posted') === 'true' ||
        formState.postedSuccessfully
      ) {
        redirectSuccessfulSubmission(pending);
        return true;
      }

      const looksLikeCustomerFormReturn =
        params.get('form_type') === 'customer' ||
        params.has('contact[email]') ||
        window.location.hash.includes('contact_form');

      if (formState.hasErrors) {
        if (panel) {
          showPanel(panel, { force: true, skipMarkShown: true });
          const form = panel.querySelector('.ml-aop__form');
          if (form) setSubmitting(form, false);
        }
        return true;
      }

      /*
       * Shopify can return an already-subscribed existing email without
       * posted_successfully? and without a normal form error. The pending
       * submit state plus a customer-form return is treated as a valid
       * existing subscriber so the visitor can continue to the offer.
       */
      if (looksLikeCustomerFormReturn) {
        redirectSuccessfulSubmission(pending);
        return true;
      }

      return false;
    };

    const selectDesignModeBlock = (event) => {
      if (!config.designMode) return;
      const panel = event.target.closest('[data-ml-aop-panel]');
      if (!panel || !root.contains(panel)) return;

      window.clearTimeout(timerId);
      showPanel(panel, { force: true, skipMarkShown: true });
    };

    const deselectDesignModeBlock = (event) => {
      if (!config.designMode || !activePanel) return;
      const panel = event.target.closest('[data-ml-aop-panel]');
      if (panel && panel === activePanel) {
        hidePanel({ userInitiated: false });
      }
    };

    const overlay = root.querySelector('[data-ml-aop-overlay]');
    if (overlay) {
      overlay.addEventListener('click', handleOverlayClick);
    }

    root.addEventListener('click', handleCloseClick);
    root.addEventListener('submit', handleSubmit);
    document.addEventListener('keydown', handleKeydown);

    if (config.designMode) {
      document.addEventListener('shopify:block:select', selectDesignModeBlock);
      document.addEventListener('shopify:block:deselect', deselectDesignModeBlock);
    }

    return {
      start() {
        if (handlePendingSubmissionReturn()) {
          return;
        }

        if (!config.masterEnabled) {
          return;
        }

        const audience = getLockedAudience(config);
        const panel = findPanel(audience);

        if (!panel) {
          return;
        }

        schedulePanel(panel);
      },

      destroy() {
        window.clearTimeout(timerId);
        if (overlay) {
          overlay.removeEventListener('click', handleOverlayClick);
        }
        root.removeEventListener('click', handleCloseClick);
        root.removeEventListener('submit', handleSubmit);
        document.removeEventListener('keydown', handleKeydown);
        document.removeEventListener(
          'shopify:block:select',
          selectDesignModeBlock
        );
        document.removeEventListener(
          'shopify:block:deselect',
          deselectDesignModeBlock
        );
      }
    };
  };

  const controllers = new WeakMap();

  const initRoot = (root) => {
    if (!root || controllers.has(root)) return;

    const config = parseConfig(root);
    if (!config) return;

    processOfferSuccessHandshake(config);

    const controller = createController(root, config);
    controllers.set(root, controller);
    controller.start();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  const boot = () => {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
      if (event.target.matches && event.target.matches(ROOT_SELECTOR)) {
        initRoot(event.target);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const root =
        event.target.matches && event.target.matches(ROOT_SELECTOR)
          ? event.target
          : event.target.querySelector(ROOT_SELECTOR);

      if (!root) return;

      const controller = controllers.get(root);
      if (controller) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
